import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, UserPlusIcon, LinkIcon, HistoryIcon, SendIcon, ZapIcon, CheckCircle2Icon, CopyIcon } from 'lucide-react';
import { useRaastTheme } from './RaastShared';
interface RaastHomeProps {
  onNavigate: (view: string) => void;
  onClose: () => void;
}
export function RaastHome({
  onNavigate,
  onClose
}: RaastHomeProps) {
  const {
    bgStyle,
    cardBg,
    textPri,
    textSec,
    borderClr,
    isDark,
    isPlatinum
  } = useRaastTheme();
  const options = [{
    id: 'register',
    title: 'Register RAAST ID',
    desc: 'Link your mobile number',
    icon: UserPlusIcon,
    view: 'register'
  }, {
    id: 'relink',
    title: 'Relink/Delink',
    desc: 'Manage your existing IDs',
    icon: LinkIcon,
    view: 'relink'
  }, {
    id: 'history',
    title: 'Transaction History',
    desc: 'View your RAAST payments',
    icon: HistoryIcon,
    view: 'history'
  }, {
    id: 'transfer',
    title: 'Pay to Merchant / Transfer',
    desc: 'Scan a QR or enter a till number',
    icon: SendIcon,
    // Opens the camera-first QR / Till payment flow
    view: 'qr_till'
  }];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2172">
      {/* Header */}
      <div className="px-6 pt-6 pb-4 flex items-center" data-id="element-2173">
        <button onClick={onClose} className={`p-2 -ml-2 rounded-full ${isDark || isPlatinum ? 'hover:bg-white/5' : 'hover:bg-black/5'}`} data-id="element-2174">
          <ArrowLeftIcon className={`w-6 h-6 ${textPri}`} strokeWidth={2} data-id="element-2175" />
        </button>
        <h1 className={`text-lg font-bold ml-3 ${textPri}`} data-id="element-2176">RAAST</h1>
      </div>

      <div className="px-6 flex-1 flex flex-col gap-4 pb-8 mt-1" data-id="element-2177">
        {/* Your RAAST ID details — shown first */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 24
      }} className={`rounded-3xl p-5 border shadow-sm ${cardBg} ${borderClr}`} data-id="element-2178">
          <div className="flex items-start justify-between mb-4" data-id="element-2179">
            <div className="flex items-center gap-3" data-id="element-2180">
              <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${isDark || isPlatinum ? 'bg-[#3D2518]' : 'bg-[#FDF3ED]'}`} data-id="element-2181">
                <ZapIcon className="w-6 h-6 text-[#F37024]" fill="currentColor" data-id="element-2182" />
              </div>
              <div data-id="element-2183">
                <p className={`text-[11px] font-medium uppercase tracking-wider ${textSec}`} data-id="element-2184">
                  Your RAAST ID
                </p>
                <p className={`text-[18px] font-bold mt-0.5 ${textPri}`} data-id="element-2185">
                  0300 1234567
                </p>
              </div>
            </div>
            <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-[#22C55E]/10 text-[#22C55E] flex items-center gap-1" data-id="element-2186">
              <CheckCircle2Icon className="w-3.5 h-3.5" data-id="element-2187" />
              Active
            </span>
          </div>

          <div className={`h-px w-full ${borderClr} mb-4`} data-id="element-2188" />

          <div className="space-y-3" data-id="element-2189">
            <div className="flex items-center justify-between" data-id="element-2190">
              <span className={`text-[13px] ${textSec}`} data-id="element-2191">Account Title</span>
              <span className={`text-[13px] font-bold ${textPri}`} data-id="element-2192">
                KASHISH AHMED
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-2193">
              <span className={`text-[13px] ${textSec}`} data-id="element-2194">Linked Account</span>
              <span className={`text-[13px] font-bold ${textPri}`} data-id="element-2195">
                Current Account ••••8910
              </span>
            </div>
            <div className="flex items-center justify-between gap-3" data-id="element-2196">
              <span className={`text-[13px] ${textSec} shrink-0`} data-id="element-2197">IBAN</span>
              <span className={`text-[12px] font-bold truncate ${textPri} flex items-center gap-1.5`} data-id="element-2198">
                PK36BOPU0000001234567890
                <CopyIcon className={`w-3.5 h-3.5 ${textSec} shrink-0`} data-id="element-2199" />
              </span>
            </div>
          </div>
        </motion.div>

        {/* Options */}
        {options.map((opt, i) => <motion.button key={opt.id} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.08 + i * 0.08,
        type: 'spring',
        stiffness: 300,
        damping: 24
      }} onClick={() => onNavigate(opt.view)} className="w-full text-left rounded-3xl p-6 relative overflow-hidden shadow-md" style={{
        background: 'linear-gradient(135deg, #F37024, #D95A1A)'
      }} data-id="element-2200">
            <div className="relative z-10 flex items-center justify-between" data-id="element-2201">
              <div data-id="element-2202">
                <h3 className="text-white font-bold text-[18px] mb-1" data-id="element-2203">
                  {opt.title}
                </h3>
                <p className="text-white/80 text-[13px]" data-id="element-2204">{opt.desc}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm" data-id="element-2205">
                <opt.icon className="w-6 h-6 text-white" strokeWidth={2} data-id="element-2206" />
              </div>
            </div>
            {/* Decorative background circle */}
            <div className="absolute -right-6 -bottom-6 w-32 h-32 rounded-full bg-white/10 blur-2xl" data-id="element-2207" />
          </motion.button>)}
      </div>
    </motion.div>;
}